/**
 * @description Layout component for the application
 */

import React from 'react';
import { PropTypes } from 'prop-types';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'

import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

import ErrorBoundary from '@seccon/shared/ui/components/ErrorBoundary';

import NavBar from './NavBar';
import { FooterProvider } from '../contexts/footer';
import { ToastProvider } from '../contexts/toast';
import { ConfirmDialogProvider } from '../contexts/confirm';
import theme from '../conf/theme';
import globalStyles from '../conf/globalStyles';

const styles = {
  appContainer: {
    height: '100vh',
    minWidth: '64rem',

    '&.migration-banner-active': {
      height: 'calc(100vh - 60px)'
    }
  },
  bodyContainer: {
    display: 'flex',
    flex: 1,
    overflowY: 'auto'
  },
  toastContainer: {
    position: 'relative',
    width: '100%'
  },
  toastPositioner: {
    bottom: '8px',
    position: 'absolute',
    right: '24px'
  }
};

/**
 * @prop children {React.Element | React.Element[]} - body react elements for the app layout to wrap
 */
const Layout = ({ children }) => {
  // A reference that points to the footer container element
  const footerRef = React.useRef(null);

  // A reference that points to the toast container element
  const toastRef = React.useRef(null);

  // This is the layout content, which is wrapped in a series of providers
  const content = (
    <Stack id='sc-container' sx={styles.appContainer}>
      <ErrorBoundary>
        <NavBar />
      </ErrorBoundary>
      <Box sx={styles.bodyContainer}>
        <ErrorBoundary>
          {children}
        </ErrorBoundary>
      </Box>
      <Box sx={styles.toastContainer}>
        <Box ref={toastRef} sx={styles.toastPositioner} />
      </Box>
      <Box ref={footerRef} />
    </Stack>
  );

  return (
    <>
      <CssBaseline />
      {globalStyles}
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <ToastProvider toastRef={toastRef}>
            <FooterProvider footerRef={footerRef}>
              <ConfirmDialogProvider>
                {content}
              </ConfirmDialogProvider>
            </FooterProvider>
          </ToastProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </>
  );
};

Layout.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.arrayOf(PropTypes.element)]).isRequired
};

export default Layout;
